import { CheckCircleOutlined } from "@ant-design/icons";
import { faRotateRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Button,
  Checkbox,
  Grid,
  Modal,
  Pagination,
  Select,
  Tag,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { FaTiktok } from "react-icons/fa";
import Router from "../";
import test_img from "../../images/32637972_7843794.jpg";
import { formatCnt } from "../utils/formatCnt";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../utils/categories";
import { useLocation, useHistory } from "react-router-dom";
import {
  useRecommendInfluencers,
  useSearchInfluencers,
} from "../hooks/useInfluencer";

const { useBreakpoint } = Grid;

const formatCategories = (categories) => {
  if (!categories) return [];
  return categories.split(",").map((cat) => cat.trim());
};

const followerOptions = [
  { label: "전체", value: null },
  { label: "~1만대", value: "10k" },
  { label: "~10만대", value: "100k" },
  { label: "10만 이상", value: "~100k" },
];

const categoryOptions = [
  { label: "전체", value: null },
  ...Object.entries(CATEGORY_MAPPING)
    .filter(([key]) => key !== "etc")
    .map(([key, label]) => ({ label, value: key })),
];

const regionOptions = [
  { label: "전체", value: null },
  { label: "캐나다", value: "ca" },
  { label: "스페인", value: "es" },
  { label: "한국", value: "kr" },
  { label: "멕시코", value: "mx" },
  { label: "미국", value: "us" },
  { label: "기타", value: "etc" },
];

const REGION_LABEL_MAPPING = {
  ca: "캐나다",
  es: "스페인",
  kr: "한국",
  mx: "멕시코",
  us: "미국",
  etc: "기타",
};

const genderOptions = [
  { label: "전체", value: null },
  { label: "여성", value: "female" },
  { label: "남성", value: "male" },
  { label: "기타", value: "other" },
];

const Chip = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      inline-flex items-center rounded-full border px-4 py-2 text-base md:text-xl font-medium
      whitespace-nowrap
      border-[#d9d9d9] transition-colors
      ${
        active
          ? "bg-[#171717] text-white border-[#6d6d6d]"
          : "bg-[#f5f5f5] text-[#171717] hover:bg-[#e6e6e6]"
      }
    `}
  >
    {children}
  </button>
);

const sortOptions = [
  { label: "팔로워 순", value: "follower" },
  { label: "가나다 순", value: "name" },
  { label: "업데이트 순", value: "updated" },
];

export const Search = () => {
  const screens = useBreakpoint();

  const [selected, setSelected] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFollower, setSelectedFollower] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedSort, setSelectedSort] = useState("follower");
  const [checkedList, setCheckedList] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false);
  const [isActiveReset, setIsActiveReset] = useState(false);

  const pageSize = 20;
  const location = useLocation();
  const history = useHistory();

  const keywordFromUrl = new URLSearchParams(location.search).get("keyword");

  const { refetch: refetchRecommended, isFetching: isRecommendedFetching } =
    useRecommendInfluencers({
      keyword: keywordFromUrl,
      follower: selectedFollower,
      category: selectedCategory,
      region: selectedRegion,
      gender: selectedGender,
    });

  const [recommendedInfluencers, setRecommendedInfluencers] = useState([]);
  const [isShowingRecommended, setIsShowingRecommended] = useState(false);

  const { data: influencersData, refetch } = useSearchInfluencers({
    page: currentPage,
    follower: selectedFollower,
    keyword: keywordFromUrl,
    category: selectedCategory,
    sort: selectedSort,
    region: selectedRegion,
    gender: selectedGender,
  });

  const influencers = influencersData?.result ?? [];
  const total = influencersData?.total ?? 0;
  const displayInfluencers = isShowingRecommended
    ? recommendedInfluencers
    : influencers;

  const allIds = displayInfluencers.map((v) => v.unique_id);
  const checkAll = allIds.length > 0 && allIds.length === checkedList.length;
  const indeterminate =
    checkedList.length > 0 && checkedList.length < allIds.length;

  const handleSelect = (index) => {
    setSelected(index);
  };

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  // 검색 URL 업데이트
  const updateSearchUrl = () => {
    if (keyword && keyword.trim()) {
      history.push(`/influencer/search?keyword=${encodeURIComponent(keyword)}`);
      setCurrentPage(1);
      setIsActiveReset(true);
    }
  };

  // 탈퇴한 크리에이터의 경우 drawer 닫고 검색 결과 갱신
  const handleInvalidInfluencer = () => {
    handleDrawerClose();
    refetch();
  };

  const handleReset = () => {
    setIsActiveReset(false);
    setKeyword("");
    setSelectedCategory(null);
    setSelectedFollower(null);
    setSelectedRegion(null);
    setSelectedGender(null);
    setCurrentPage(1);
    setSelectedSort("follower");
    setRecommendedInfluencers([]);
    setIsShowingRecommended(false);
    history.replace("/influencer/search");
  };

  // URL의 keyword가 변경될 때 keyword 상태를 업데이트
  useEffect(() => {
    setKeyword(keywordFromUrl);
  }, [keywordFromUrl]);

  useEffect(() => {
    setCheckedList([]);
  }, [
    currentPage,
    isShowingRecommended,
    selectedCategory,
    selectedFollower,
    selectedRegion,
    selectedGender,
    selectedSort,
    keywordFromUrl,
  ]);

  useEffect(() => {
    setRecommendedInfluencers([]);
    setIsShowingRecommended(false);
  }, [
    selectedCategory,
    selectedFollower,
    selectedRegion,
    selectedGender,
    selectedSort,
    keywordFromUrl,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlInfluencerId = params.get("influencer_id");

    if (urlInfluencerId) {
      setSelectedInfluencer({ unique_id: urlInfluencerId });
      setDrawerVisible(true);
    } else {
      setDrawerVisible(false);
      setSelectedInfluencer(null);
    }
  }, [location.search]);

  const handleSortClick = (value) => {
    setIsActiveReset(true);
    setSelectedSort(value);
    setCurrentPage(1);
  };

  const handleFollowerClick = (value) => {
    setIsActiveReset(true);
    setSelectedFollower(value === selectedFollower ? null : value);
    setCurrentPage(1);
  };

  const handleCategoryClick = (value) => {
    setIsActiveReset(true);
    setSelectedCategory(value === selectedCategory ? null : value);
    setCurrentPage(1);
  };

  const handleGenderClick = (value) => {
    setIsActiveReset(true);
    setSelectedGender(value === selectedGender ? null : value);
    setCurrentPage(1);
  };

  const handleRegionChange = (value) => {
    setIsActiveReset(true);
    setSelectedRegion(value);
    setCurrentPage(1);
  };

  const handleRowClick = (influencer) => {
    const params = new URLSearchParams(location.search);
    params.set("influencer_id", influencer.unique_id);
    history.replace({ search: params.toString() });
  };

  const handleDrawerClose = () => {
    const params = new URLSearchParams(location.search);
    params.delete("influencer_id");
    history.replace({ search: params.toString() });
  };

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      setCheckedList(allIds);
    } else {
      setCheckedList([]);
    }
  };

  const handleCheck = (e, influencerId) => {
    if (e.target.checked) {
      setCheckedList((prev) => [...prev, influencerId]);
    } else {
      setCheckedList((prev) => prev.filter((id) => id !== influencerId));
    }
  };

  const handleCampaignInvite = () => {
    if (selectedInfluencers.length === 0) {
      message.info("크리에이터를 선택해주세요.");
      return;
    }

    const influencersWithoutEmail = selectedInfluencers.filter(
      (influencer) => !influencer.email
    );

    if (influencersWithoutEmail.length > 0) {
      const names = influencersWithoutEmail
        .map((influencer) => influencer.nickname || influencer.unique_id)
        .join(", ");

      message.warning(
        `이메일이 없는 크리에이터는 초대할 수 없습니다: ${names}`
      );
      return;
    }
    setInviteModalVisible(true);
  };

  const handleInviteModalClose = () => {
    setInviteModalVisible(false);
  };

  // 선택된 인플루언서 객체 배열 생성
  const selectedInfluencers = displayInfluencers.filter((influencer) =>
    checkedList.includes(influencer.unique_id)
  );

  const selectedConditions = [
    keyword?.trim() ? `키워드: ${keyword.trim()}` : null,
    selectedCategory ? `카테고리: ${CATEGORY_MAPPING[selectedCategory]}` : null,
    selectedFollower ? `팔로워: ${selectedFollower}` : null,
    selectedRegion ? `지역: ${REGION_LABEL_MAPPING[selectedRegion]}` : null,
    selectedGender
      ? `성별: ${genderOptions.find((opt) => opt.value === selectedGender)?.label}`
      : null,
  ].filter(Boolean);

  const openRecommendModal = () => {
    setIsRecommendModalOpen(true);
  };

  const closeRecommendModal = () => {
    setIsRecommendModalOpen(false);
  };

  const handleRecommendInfluencer = async () => {
    const { data } = await refetchRecommended();
    setRecommendedInfluencers(data || []);
    setIsShowingRecommended(true);
    setIsActiveReset(true);
    closeRecommendModal();
  };

  return (
    <div>
      <h2 className="md:text-4xl text-3xl font-bold">크리에이터 찾기</h2>
      <div className="flex gap-2 items-center mb-8">
        <button
          className={`social-icon ${selected === 0 ? "active" : ""}`}
          onClick={() => handleSelect(0)}
        >
          <FaTiktok />
          <span className="ml-2 text-base md:text-xl">TikTok</span>
        </button>
      </div>

      <div className="flex gap-3 md:gap-4 w-full md:max-w-[600px]">
        <div className="flex-1 flex">
          <input
            type="text"
            placeholder="키워드를 입력하세요"
            value={keyword}
            onChange={handleKeywordChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                updateSearchUrl();
              }
            }}
            className="w-full h-[37px] border border-gray-300 border-r-0 rounded-l-lg px-[7px] py-[11px] outline-none text-lg md:text-xl"
          />
          <button
            onClick={updateSearchUrl}
            className="w-[60px] md:w-[80px] h-[37px] px-3 md:px-4 py-2 bg-[#171717] hover:bg-[#333333] text-white rounded-r-lg text-lg md:text-xl flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
        <button
          disabled={!isActiveReset}
          onClick={handleReset}
          // className="px-4 py-2 rounded-lg bg-[#171717] hover:bg-[#333333] text-white text-lg md:text-xl flex items-center justify-center h-[37px]"
          className={`px-4 py-2 rounded-lg bg-[#171717] text-white text-lg md:text-xl flex items-center justify-center h-[37px] ${!isActiveReset ? "cursor-not-allowed opacity-50" : "hover:bg-[#333333]"}`}
        >
          <FontAwesomeIcon icon={faRotateRight} />
        </button>
      </div>

      {/* 필터 */}
      <div className="mt-10 mb-5">
        <div className="bg-white border border-[#ddd] rounded-md py-[18px] px-[32px]">
          <ul className="space-y-6 items-center md:grid md:grid-cols-[auto_1fr] md:gap-x-[32px] md:gap-y-6 md:space-y-0">
            {/* 팔로워 */}
            <li className="flex flex-col md:contents gap-4 md:gap-0">
              <span className="text-lg md:text-2xl font-semibold text-[#171717] shrink-0 whitespace-nowrap md:col-start-1">
                팔로워
              </span>
              <div className="md:col-start-2">
                <div className="flex flex-wrap gap-2">
                  {followerOptions.map((item) => (
                    <Chip
                      key={item.label}
                      active={selectedFollower === item.value}
                      onClick={() => handleFollowerClick(item.value)}
                    >
                      {item.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </li>

            <li className="flex flex-col md:contents gap-2 md:gap-0">
              <span className="text-lg md:text-2xl font-semibold text-[#171717] shrink-0 whitespace-nowrap md:col-start-1">
                카테고리
              </span>
              <div className="md:col-start-2">
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.map((item) => (
                    <Chip
                      key={item.label}
                      active={selectedCategory === item.value}
                      onClick={() => handleCategoryClick(item.value)}
                    >
                      {item.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </li>

            <li className="flex flex-col md:contents gap-2 md:gap-0">
              <span className="text-lg md:text-2xl font-semibold text-[#171717] shrink-0 whitespace-nowrap md:col-start-1">
                성별
              </span>
              <div className="md:col-start-2">
                <div className="flex flex-wrap gap-2">
                  {genderOptions.map((item) => (
                    <Chip
                      key={item.label}
                      active={selectedGender === item.value}
                      onClick={() => handleGenderClick(item.value)}
                    >
                      {item.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </li>

            <li className="flex flex-col md:contents gap-2 md:gap-0">
              <span className="text-lg md:text-2xl font-semibold text-[#171717] shrink-0 whitespace-nowrap md:col-start-1">
                국가
              </span>
              <div className="md:col-start-2">
                <Select
                  value={selectedRegion}
                  onChange={handleRegionChange}
                  options={regionOptions}
                  className={[
                    "w-full max-w-[220px]",
                    "[&_.ant-select-selector]:!rounded-full",
                    "[&_.ant-select-selector]:!border-[#d9d9d9]",
                    "[&_.ant-select-selector]:!bg-[#f8f8f8]",
                    "[&_.ant-select-selector]:!px-4",
                    "[&_.ant-select-selector]:!shadow-none",
                    "[&_.ant-select-selection-item]:!text-lg",
                    "md:[&_.ant-select-selection-item]:!text-2xl",
                    "[&_.ant-select-selection-placeholder]:!text-lg",
                    "md:[&_.ant-select-selection-placeholder]:!text-2xl",
                    "[&_.ant-select-selection-placeholder]:!text-[#666]",
                  ].join(" ")}
                  allowClear={false}
                  size={screens.md ? "middle" : "small"}
                />
              </div>
            </li>

            {/* 정렬 */}
            <li className="flex flex-col md:contents gap-2 md:gap-0">
              <span className="text-lg md:text-2xl font-semibold text-[#171717] shrink-0 whitespace-nowrap md:col-start-1">
                정렬
              </span>
              <div className="md:col-start-2">
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((item) => (
                    <Chip
                      key={item.value}
                      active={selectedSort === item.value}
                      onClick={() => handleSortClick(item.value)}
                    >
                      {item.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-[20px] mt-[20px] flex justify-between items-center">
        <Button
          type="primary"
          className="bg-[#171717] hover:!bg-[#333333] text-white text-base md:text-lg"
          onClick={handleCampaignInvite}
        >
          캠페인 초대하기
        </Button>
        <Button
          type="primary"
          className="bg-[#F9A43F] hover:!bg-[#F9A43F] border-none text-white text-base md:text-lg hover:!text-white focus:!bg-[#F9A43F] focus:!text-white active:!bg-[#F9A43F] active:!text-white"
          onClick={openRecommendModal}
          loading={isRecommendedFetching}
        >
          AI 추천 받기
        </Button>
      </div>

      {isShowingRecommended ? (
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-[15px] font-semibold text-[#111827]">
              AI 추천 크리에이터
            </p>
            <p className="mt-1 text-[13px] text-[#6b7280]">
              검색 조건을 기준으로 추천되었습니다.
            </p>
          </div>
        </div>
      ) : null}

      <div className="w-full my-5 mb-[30px] rounded-[12px] overflow-x-auto bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)]">
        <table className="w-full border-collapse text-[#171717] bg-white text-center font-normal">
          <thead className="bg-[#171717] text-white text-lg md:text-xl">
            <tr className="shrink-0">
              <th className="w-[5%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                <Checkbox
                  checked={checkAll}
                  indeterminate={indeterminate}
                  onChange={(e) => handleCheckAll(e)}
                />
              </th>
              <th className="w-[5%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                프로필
              </th>
              <th className="w-[10%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                아이디
              </th>
              <th className="w-[10%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                닉네임
              </th>
              <th className="w-[20%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                카테고리
              </th>
              <th className="w-[10%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                팔로워
              </th>
              <th className="w-[5%] py-[14px] px-[8px] font-semibold border-b border-[#ddd] whitespace-nowrap">
                국가
              </th>
            </tr>
          </thead>
          <tbody className="text-lg md:text-xl">
            {displayInfluencers.length > 0 ? (
              displayInfluencers.map((influencer, index) => (
                <tr
                  key={index}
                  onClick={() => handleRowClick(influencer)}
                  className="cursor-pointer hover:bg-[#e6e6e6]"
                >
                  <td
                    className="py-[12px] px-[8px] border-b border-[#eee]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={checkedList.includes(influencer.unique_id)}
                      onChange={(e) => handleCheck(e, influencer.unique_id)}
                    />
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    <div className="relative inline-block shrink-0 w-[32px] h-[32px] md:w-[40px] md:h-[40px]">
                      <img
                        src={influencer.profile_img || test_img}
                        alt=""
                        className="rounded-full w-full h-full object-cover object-center bg-black border border-[#ddd]"
                      />
                      {influencer.is_registered && (
                        <CheckCircleOutlined
                          className="absolute bottom-0 right-0 text-[#52c41a] text-sm md:text-base bg-white rounded-full"
                          title="인증된 사용자"
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    <div className="flex items-center gap-1 justify-center">
                      {influencer.unique_id || "-"}
                      {influencer.is_registered && (
                        <CheckCircleOutlined
                          className="text-[#52c41a] text-sm"
                          title="인증된 사용자"
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    {influencer.nickname || "-"}
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    <div className="flex gap-1 flex-wrap justify-center items-center min-h-full whitespace-nowrap">
                      {formatCategories(influencer.categories)
                        ? formatCategories(influencer.categories).map(
                            (category, idx) => (
                              <span
                                key={idx}
                                className="px-[10px] py-[2px] md:px-[12px] md:py-[4px] rounded-[5px] text-lg md:text-xl inline-block m-0.5 font-semibold"
                                style={{
                                  ...(CATEGORY_STYLES[category] ||
                                    CATEGORY_STYLES.etc),
                                  border: "none",
                                  opacity: 1,
                                }}
                              >
                                {CATEGORY_MAPPING[category] || category}
                              </span>
                            )
                          )
                        : []}
                    </div>
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    {formatCnt(influencer.follower_count)}
                  </td>
                  <td className="py-[12px] px-[8px] border-b border-[#eee]">
                    {REGION_LABEL_MAPPING[influencer.region] ??
                      influencer.region ??
                      "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="py-[32px] px-[12px] text-center text-[15px] text-[#94a3b8]"
                >
                  {isShowingRecommended
                    ? "추천된 크리에이터가 없습니다."
                    : null}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isShowingRecommended ? (
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          className="mt-12 justify-center"
        />
      ) : null}

      <Router.InfluencerDetailDrawer
        visible={drawerVisible}
        onClose={handleDrawerClose}
        influencer={selectedInfluencer}
        onInvalidUser={handleInvalidInfluencer}
      />

      <Router.InviteCampaign
        visible={inviteModalVisible}
        onClose={handleInviteModalClose}
        selectedInfluencers={selectedInfluencers}
      />

      <Modal
        open={isRecommendModalOpen}
        onCancel={closeRecommendModal}
        onOk={handleRecommendInfluencer}
        title="AI 추천"
        footer={[
          <Button
            key="cancel"
            type="default"
            onClick={closeRecommendModal}
            className="border-[#d9d9d9] text-[#666] hover:!border-[#d9d9d9] hover:!text-[#666] hover:!bg-white focus:!border-[#d9d9d9] focus:!text-[#666] active:!border-[#d9d9d9]"
          >
            취소
          </Button>,
          <Button
            key="ok"
            loading={isRecommendedFetching}
            className="bg-[#171717] hover:!bg-[#171717] border-none text-white hover:!text-white focus:!bg-[#171717] focus:!text-white active:!bg-[#171717] active:!text-white"
            onClick={handleRecommendInfluencer}
          >
            추천 받기
          </Button>,
        ]}
        confirmLoading={isRecommendedFetching}
      >
        {selectedConditions.length > 0 ? (
          <>
            <Badge wrap>
              {selectedConditions.map((condition) => (
                <Tag key={condition}>{condition}</Tag>
              ))}
            </Badge>
            <p className="mt-4">
              선택한 조건을 기반으로 AI가 적합한 인플루언서를 추천합니다.
            </p>
          </>
        ) : (
          <p className="text-[14px] text-[#666]">
            선택된 조건 없이 전체 기준으로 추천을 진행합니다.
          </p>
        )}
      </Modal>
    </div>
  );
};
